import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Spinner, Empty, StatusBadge, History } from "@/components/common";
import { useApi, errMsg, fmtDate, sar } from "@/lib/api";
import { MAINT_STATUS, ORDER_STATUS, URGENCY } from "@/lib/labels";
import { MaintenancePhotos } from "@/components/Photos";

const NEXT = {
  maintenance: { assigned: "in_progress", in_progress: "completed" },
  orders: { assigned: "on_the_way", on_the_way: "completed" },
};

const TaskCard = ({ t, kind, onUpdate }) => {
  const [note, setNote] = useState("");
  const map = kind === "maintenance" ? MAINT_STATUS : ORDER_STATUS;
  const next = NEXT[kind][t.status];
  return (
    <div className="min-w-0 rounded-2xl border border-[#E5DEC9] bg-white p-6" data-testid={`task-card-${t.code}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs text-[#5C7067] ltr-num">{t.code}</p>
          <h3 className="font-bold text-[#0F382C]">{kind === "maintenance" ? t.service_name : `طلب متجر · ${sar(t.total)}`}</h3>
          <p className="text-xs text-[#5C7067]">{fmtDate(t.created_at)}{t.urgency && ` · ${URGENCY[t.urgency]}`}</p>
        </div>
        <StatusBadge map={map} status={t.status} testId={`task-status-${t.code}`} />
      </div>
      <div className="mt-3 space-y-1 text-sm">
        <p className="flex items-center gap-1"><MapPin className="h-4 w-4 text-[#C59B27]" />{t.project_name} — {kind === "maintenance" ? t.location : t.unit}</p>
        <p className="flex items-center gap-1"><Phone className="h-4 w-4 text-[#C59B27]" />{t.owner_name} · <a className="ltr-num underline" href={`tel:${t.owner_phone}`}>{t.owner_phone}</a></p>
        {kind === "maintenance" ? <><p>{t.description}</p><MaintenancePhotos task={t} /></> : (
          <ul className="list-disc pr-5">{t.items.map((i, k) => <li key={k}>{i.name} × {i.qty}{i.with_installation && " (تركيب)"}</li>)}</ul>
        )}
        {t.preferred_date && <p className="text-[#5C7067]">الموعد المفضل: {t.preferred_date.replace("T", " ")}</p>}
      </div>
      {next && (
        <div className="mt-4 space-y-2">
          <Textarea placeholder="ملاحظة (اختياري)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`task-note-${t.code}`} />
          <Button onClick={async () => { if (await onUpdate(kind, t, next, note)) setNote(""); }} className="w-full rounded-full bg-[#0F382C] hover:bg-[#164E3D]" data-testid={`task-advance-${t.code}`}>
            تحويل إلى: {map[next].label}
          </Button>
        </div>
      )}
      <details className="mt-3"><summary className="cursor-pointer text-sm font-semibold text-[#0F382C]">السجل</summary><div className="mt-3"><History items={t.history} map={map} /></div></details>
    </div>
  );
};

const List = ({ items, kind, onUpdate }) =>
  items.length === 0 ? <Empty icon={ClipboardList} title="لا توجد مهام مسندة إليك" testId={`employee-empty-${kind}`} /> : (
    <div className="grid gap-4 lg:grid-cols-2">{items.map((t) => <TaskCard key={t.id} t={t} kind={kind} onUpdate={onUpdate} />)}</div>
  );

export default function EmployeeTasks() {
  const api = useApi();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api("get", "/employee/tasks").then(setData).catch((e) => toast.error(errMsg(e)));
  }, [api]);
  useEffect(() => {
    load();
  }, [load]);
  const update = async (kind, t, status, note) => {
    try {
      await api("patch", `/employee/${kind}/${t.id}`, { status, note });
      toast.success("تم تحديث الحالة");
      load();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  if (!data) return <Spinner />;
  const perms = data.permissions;
  return (
    <div data-testid="employee-page">
      <PageHeader title="مهامي" subtitle="المهام المسندة إليك فقط حسب صلاحياتك" />
      {perms.length === 0 ? <Empty icon={ClipboardList} title="لا توجد صلاحيات مفعلة لحسابك" text="تواصل مع مدير النظام" testId="employee-no-perms" /> : (
        <Tabs dir="rtl" defaultValue={perms[0]}>
          <TabsList className="mb-6">
            {perms.includes("maintenance") && <TabsTrigger value="maintenance" data-testid="employee-tab-maintenance">الصيانة ({data.maintenance.length})</TabsTrigger>}
            {perms.includes("orders") && <TabsTrigger value="orders" data-testid="employee-tab-orders">طلبات المتجر ({data.orders.length})</TabsTrigger>}
          </TabsList>
          <TabsContent value="maintenance"><List items={data.maintenance} kind="maintenance" onUpdate={update} /></TabsContent>
          <TabsContent value="orders"><List items={data.orders} kind="orders" onUpdate={update} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
